/**
 * EsocialWizard — Passo-a-passo visual do onboarding eSocial.
 *
 * Mostra o status de cada etapa exigida antes da empresa estar
 * habilitada a transmitir eventos remuneratórios (S-1200/S-1210/S-1299):
 *
 *   1. Certificado digital (e-CNPJ ou e-CPF) ativo
 *   2. S-1000 Informações do Empregador
 *   3. S-1005 Tabela de Estabelecimentos
 *   4. S-1020 Lotação Tributária
 *   5. S-1010 Rubricas (≥ 1)
 *   6. S-1070 Processos (opcional)
 *   7. S-2200 Admissão (≥ 1, para liberar S-1200)
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  CheckCircle2, Clock, AlertCircle, ShieldCheck, Building2, FileText, MapPin,
  BookOpenCheck, AlertTriangle, UserPlus, ArrowRight,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// ---------------------------------------------------------------------------
// Util: classifica o status de um conjunto de eventos do mesmo tipo
// ---------------------------------------------------------------------------
const PROC_STATUSES = ['processado_ok', 'transmitido', 'aceito'];

const getEventoStatus = (eventos, tipo) => {
  const lista = eventos.filter((e) => e.tipo === tipo);
  if (lista.length === 0) return 'pending';
  if (lista.some((e) => PROC_STATUSES.includes(e.status))) return 'done';
  if (lista.some((e) => e.status === 'rejeitado')) return 'error';
  return 'in_progress';
};

// ---------------------------------------------------------------------------
// Step card
// ---------------------------------------------------------------------------
const STATUS_META = {
  done:        { color: 'bg-emerald-600',  ring: 'ring-emerald-300', icon: CheckCircle2, label: 'Concluído' },
  in_progress: { color: 'bg-amber-500',    ring: 'ring-amber-300',   icon: Clock,         label: 'Em andamento' },
  pending:     { color: 'bg-slate-300',    ring: 'ring-slate-200',   icon: Clock,         label: 'Pendente' },
  error:       { color: 'bg-red-600',      ring: 'ring-red-300',     icon: AlertCircle,   label: 'Erro' },
};

const StepCard = ({ number, title, description, status, icon: Icon, optional, onJump, testid }) => {
  const meta = STATUS_META[status];
  const StatusIcon = meta.icon;
  return (
    <button
      type="button"
      onClick={onJump}
      data-testid={testid}
      className={`group w-full text-left border rounded-lg p-3 transition shadow-sm
        ${status === 'done'
          ? 'bg-emerald-50/50 border-emerald-200 hover:border-emerald-300'
          : status === 'error'
          ? 'bg-red-50/40 border-red-200 hover:border-red-300'
          : 'bg-white border-slate-200 hover:border-indigo-300'} `}
    >
      <div className="flex items-start gap-3">
        {/* Step number with status ring */}
        <div className={`relative shrink-0 w-10 h-10 rounded-full flex items-center justify-center
                         text-white font-semibold ring-4 ${meta.color} ${meta.ring}`}>
          {status === 'done' ? <CheckCircle2 className="h-5 w-5" /> : number}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Icon className="h-4 w-4 text-indigo-600" />
            <span className="font-semibold text-sm text-slate-800">{title}</span>
            {optional && (
              <Badge variant="outline" className="text-[10px] py-0 px-1 border-slate-300 text-slate-500">
                Opcional
              </Badge>
            )}
            <Badge className={`${meta.color} text-white text-[10px] gap-1 ml-auto`}>
              <StatusIcon className="h-3 w-3" /> {meta.label}
            </Badge>
          </div>
          <p className="text-xs text-slate-500 mt-1 pr-2">{description}</p>
        </div>

        <ArrowRight className="h-4 w-4 text-slate-300 mt-3 group-hover:text-indigo-500 transition" />
      </div>
    </button>
  );
};

// ---------------------------------------------------------------------------
// Wizard principal
// ---------------------------------------------------------------------------
const EsocialWizard = ({ companyId, onJumpToCert, onJumpToCadastro }) => {
  const [eventos, setEventos] = useState([]);
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [evRes, certRes] = await Promise.all([
        axios.get(`${API}/api/esocial/cadastro/${companyId}`, auth()).catch(() => ({ data: [] })),
        axios.get(`${API}/api/esocial/certificados`, auth()).catch(() => ({ data: [] })),
      ]);
      setEventos(Array.isArray(evRes.data) ? evRes.data : []);
      setCerts(Array.isArray(certRes.data) ? certRes.data : []);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { reload(); }, [reload]);

  // Status calculation
  const hasCert = certs.some((c) => c.ativo);
  const certStatus = hasCert ? 'done' : 'pending';
  const s1000 = getEventoStatus(eventos, 'S-1000');
  const s1005 = getEventoStatus(eventos, 'S-1005');
  const s1020 = getEventoStatus(eventos, 'S-1020');
  const s1010 = getEventoStatus(eventos, 'S-1010');
  const s1070 = getEventoStatus(eventos, 'S-1070');
  const s2200 = getEventoStatus(eventos, 'S-2200');

  // Overall progress percentage (excluding optional S-1070)
  const mandatory = [certStatus, s1000, s1005, s1020, s1010, s2200];
  const done = mandatory.filter((s) => s === 'done').length;
  const pct = Math.round((done / mandatory.length) * 100);

  if (!companyId) return null;

  return (
    <Card className="border-indigo-100 bg-gradient-to-br from-indigo-50/40 to-white"
          data-testid="esocial-wizard">
      <CardContent className="p-4 space-y-4">
        {/* Header com progresso */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="font-semibold text-base text-slate-900 flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-indigo-600" />
              Onboarding eSocial — Passo a Passo
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Complete os passos abaixo para habilitar a empresa a transmitir folha mensal ao gov.br.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-2xl font-bold text-indigo-700 leading-none">
                {pct}<span className="text-base text-indigo-400">%</span>
              </div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">
                {done}/{mandatory.length} obrigatórios
              </div>
            </div>
            <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-indigo-500 to-emerald-500 transition-all"
                   style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>

        {loading && (
          <div className="text-xs text-slate-500">Carregando status…</div>
        )}

        <div className="grid gap-2">
          <StepCard number="1" title="Certificado Digital"
                    description="Carregue o e-CNPJ (e opcionalmente o e-CPF do representante legal)."
                    status={certStatus} icon={ShieldCheck} onJump={onJumpToCert}
                    testid="wizard-step-cert" />

          <StepCard number="2" title="S-1000 — Empregador"
                    description="Informações cadastrais da empresa (classe tributária, contato)."
                    status={s1000} icon={Building2} onJump={() => onJumpToCadastro('S-1000')}
                    testid="wizard-step-s1000" />

          <StepCard number="3" title="S-1005 — Estabelecimento"
                    description="CNAE preponderante e dados do estabelecimento (matriz/filial)."
                    status={s1005} icon={FileText} onJump={() => onJumpToCadastro('S-1005')}
                    testid="wizard-step-s1005" />

          <StepCard number="4" title="S-1020 — Lotação Tributária"
                    description="Define FPAS e código de terceiros para o estabelecimento."
                    status={s1020} icon={MapPin} onJump={() => onJumpToCadastro('S-1020')}
                    testid="wizard-step-s1020" />

          <StepCard number="5" title="S-1010 — Rubricas"
                    description="Cadastre cada rubrica usada na folha (salário, INSS, IRRF, FGTS, etc.)."
                    status={s1010} icon={BookOpenCheck} onJump={() => onJumpToCadastro('S-1010')}
                    testid="wizard-step-s1010" />

          <StepCard number="6" title="S-1070 — Processos" optional
                    description="Cadastre processos administrativos/judiciais que afetem tributos."
                    status={s1070} icon={AlertTriangle} onJump={() => onJumpToCadastro('S-1070')}
                    testid="wizard-step-s1070" />

          <StepCard number="7" title="S-2200 — Admissão"
                    description="Cadastre a admissão de cada trabalhador CLT (≥ 1 para liberar S-1200)."
                    status={s2200} icon={UserPlus} onJump={() => onJumpToCadastro('S-2200')}
                    testid="wizard-step-s2200" />
        </div>

        {pct === 100 && (
          <div className="border border-emerald-300 bg-emerald-50 rounded-md p-3 text-xs text-emerald-800 flex items-center gap-2"
               data-testid="wizard-complete-banner">
            <CheckCircle2 className="h-4 w-4" />
            <span>
              <b>Tudo pronto!</b> A empresa está habilitada para transmitir S-1200/S-1210/S-1299 mensalmente.
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EsocialWizard;
